import { useState, useCallback, useRef, useEffect } from 'react';
import type {
  Worksheet,
  ContextGroup,
  MeasurementGroup,
  FindingGroup,
  FlagGroup,
  FollowUpGroup,
  TemplateSection,
} from '@/models/template';

interface WorksheetEditorState {
  worksheet: Worksheet | null;
  unsavedChanges: boolean;
  updateName: (name: string) => void;
  updateSections: (sections: TemplateSection[]) => void;
  updateContextGroups: (groups: ContextGroup[]) => void;
  updateMeasurementGroups: (groups: MeasurementGroup[]) => void;
  updateFindingGroups: (groups: FindingGroup[]) => void;
  updateFlagGroups: (groups: FlagGroup[]) => void;
  updateFollowUpGroups: (groups: FollowUpGroup[]) => void;
  getPayload: () => Worksheet | null;
  resetToServer: () => void;
}

export function useWorksheetEditorState(
  serverData: Worksheet | undefined
): WorksheetEditorState {
  const [local, setLocal] = useState<Worksheet | null>(null);
  const serverRef = useRef<Worksheet | null>(null);

  // Keep serverRef current with the latest server data so the unsavedChanges
  // diff and resetToServer always compare against what's actually on the
  // server. Only reset local state when switching to a different worksheet.
  useEffect(() => {
    if (!serverData) return;
    const isDifferentWorksheet = serverRef.current?.uuid !== serverData.uuid;
    serverRef.current = serverData;
    if (isDifferentWorksheet) {
      setLocal(structuredClone(serverData));
    }
  }, [serverData]);

  const unsavedChanges =
    local !== null &&
    serverRef.current !== null &&
    JSON.stringify(local) !== JSON.stringify(serverRef.current);

  const update = useCallback((partial: Partial<Worksheet>) => {
    setLocal((prev) => (prev ? { ...prev, ...partial } : prev));
  }, []);

  const updateName = useCallback((name: string) => update({ name }), [update]);
  const updateSections = useCallback(
    (sections: TemplateSection[]) => update({ sections }),
    [update]
  );
  const updateContextGroups = useCallback(
    (contextGroups: ContextGroup[]) => update({ contextGroups }),
    [update]
  );
  const updateMeasurementGroups = useCallback(
    (measurementGroups: MeasurementGroup[]) => update({ measurementGroups }),
    [update]
  );
  const updateFindingGroups = useCallback(
    (findingGroups: FindingGroup[]) => update({ findingGroups }),
    [update]
  );
  const updateFlagGroups = useCallback(
    (flagGroups: FlagGroup[]) => update({ flagGroups }),
    [update]
  );
  const updateFollowUpGroups = useCallback(
    (followUpGroups: FollowUpGroup[]) => update({ followUpGroups }),
    [update]
  );

  const getPayload = useCallback(() => local, [local]);

  const resetToServer = useCallback(() => {
    if (serverRef.current) {
      setLocal(structuredClone(serverRef.current));
    }
  }, []);

  return {
    worksheet: local,
    unsavedChanges,
    updateName,
    updateSections,
    updateContextGroups,
    updateMeasurementGroups,
    updateFindingGroups,
    updateFlagGroups,
    updateFollowUpGroups,
    getPayload,
    resetToServer,
  };
}
